import React, { useEffect, useRef, useState } from "react";

/**
 * BotGenomix Arena — the survival-of-the-fittest visualiser.
 *
 * Three zones (left→right on the canvas):
 *   1. Graveyard   — 60% of bots die here. Tombstones with strategy names.
 *   2. Battlefield — surviving bots march right, occasionally getting sniped.
 *   3. Podium      — top 3 reach Champion / Elite / Elite tiers.
 *
 * Background: tech-grid + slow radial scanline + faint circuit traces.
 * Bots: pixel-mech drawings (head, body, tracks, antenna) — not orbs.
 */
const SYMS = [
  "EURUSD", "GBPUSD", "XAUUSD", "GER40", "US30", "AUDUSD",
  "EURJPY", "GBPJPY", "NAS100", "BTCUSD", "USDCAD", "USDJPY",
  "SPX500", "UK100", "OIL", "SILVER",
];
const FAMILIES = ["MR", "TF", "VB", "SR"];

// deterministic pseudo-random for stable per-bot styling
const seeded = (s) => { let x = 0; for (let i = 0; i < s.length; i++) x = (x * 31 + s.charCodeAt(i)) & 0xffffffff; return () => ((x = (x * 1664525 + 1013904223) & 0xffffffff) >>> 0) / 0xffffffff; };

export default function BotArena() {
  const canvasRef = useRef(null);
  const rafRef = useRef(0);
  const stateRef = useRef({ bots: [], graves: [], podium: [], sparks: [], t: 0, shake: 0 });
  const [stats, setStats] = useState({ dead: 0, alive: 0, champs: 0 });

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");

    function resize() {
      const dpr = Math.min(window.devicePixelRatio || 1, 2);
      canvas.width = canvas.offsetWidth * dpr;
      canvas.height = canvas.offsetHeight * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    }
    resize();
    const ro = new ResizeObserver(resize);
    ro.observe(canvas);

    // spawn initial batch of hopefuls
    const state = stateRef.current;
    state.bots = [];
    state.graves = [];
    state.podium = [];
    state.sparks = [];

    const spawn = () => {
      const sym = SYMS[Math.floor(Math.random() * SYMS.length)];
      const fam = FAMILIES[Math.floor(Math.random() * FAMILIES.length)];
      const rnd = seeded(sym + fam + Math.random());
      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;
      return {
        name: `${fam}_${sym}`,
        sym, fam,
        x: 60 + rnd() * 40,                  // spawn on the far left, past graveyard
        y: 60 + rnd() * (canvas.offsetHeight * 0.55),   // upper 60% of arena, above graves
        vx: 0.7 + rnd() * 1.1,               // fast, so we see them cross
        vy: 0,
        hp: 55 + Math.floor(rnd() * 65),     // some survive to reach podium
        rnd,
        color: fam === "MR" ? "#22D3EE"
             : fam === "TF" ? "#D6FF38"
             : fam === "VB" ? "#FB923C"
             :                "#E879F9",
        blinkPhase: rnd() * Math.PI * 2,
        limbPhase:  rnd() * Math.PI * 2,
        deathAt: -1,
      };
    };

    for (let i = 0; i < 14; i++) state.bots.push(spawn());

    let last = performance.now();

    function frame(now) {
      const dt = Math.min((now - last) / 16.6, 3);
      last = now;
      state.t += dt;
      const w = canvas.offsetWidth;
      const h = canvas.offsetHeight;

      // ---- Background: gradient + tech grid + scanline sweep + circuit traces
      const bg = ctx.createLinearGradient(0, 0, 0, h);
      bg.addColorStop(0, "#04060A");
      bg.addColorStop(0.5, "#080B12");
      bg.addColorStop(1, "#02030530");
      ctx.fillStyle = "#04060A";
      ctx.fillRect(0, 0, w, h);
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, w, h);

      // grid
      ctx.strokeStyle = "rgba(45,55,72,0.18)";
      ctx.lineWidth = 1;
      const cell = 28;
      for (let x = 0; x < w; x += cell) { ctx.beginPath(); ctx.moveTo(x, 0); ctx.lineTo(x, h); ctx.stroke(); }
      for (let y = 0; y < h; y += cell) { ctx.beginPath(); ctx.moveTo(0, y); ctx.lineTo(w, y); ctx.stroke(); }

      // circuit traces (static pseudo-random)
      ctx.strokeStyle = "rgba(214,255,56,0.06)";
      ctx.lineWidth = 1;
      for (let i = 0; i < 8; i++) {
        const seed = seeded("circuit" + i);
        ctx.beginPath();
        let cx = seed() * w, cy = seed() * h;
        ctx.moveTo(cx, cy);
        for (let k = 0; k < 4; k++) {
          cx += (seed() - 0.5) * 120;
          cy += (seed() - 0.5) * 120;
          ctx.lineTo(cx, cy);
        }
        ctx.stroke();
      }

      // sweeping horizontal scanline
      const sweepY = (state.t * 0.6) % (h + 60) - 30;
      const scan = ctx.createLinearGradient(0, sweepY - 40, 0, sweepY + 40);
      scan.addColorStop(0, "rgba(34,211,238,0)");
      scan.addColorStop(0.5, "rgba(34,211,238,0.08)");
      scan.addColorStop(1, "rgba(34,211,238,0)");
      ctx.fillStyle = scan;
      ctx.fillRect(0, sweepY - 40, w, 80);

      // vertical data streams (matrix-style falling code)
      ctx.font = "10px ui-monospace, monospace";
      for (let i = 0; i < 12; i++) {
        const sx = (i * 137 + state.t * 0.3) % w;
        const streamY = ((state.t * 1.2 + i * 90) % (h + 200)) - 100;
        for (let k = 0; k < 8; k++) {
          const alpha = Math.max(0, 0.45 - k * 0.06);
          ctx.fillStyle = `rgba(45, 212, 191, ${alpha})`;
          const ch = ((i * 7 + k * 3 + Math.floor(state.t / 4)) % 2) ? "1" : "0";
          ctx.fillText(ch, sx, streamY + k * 14);
        }
      }

      // corner HUD ticks
      ctx.strokeStyle = "rgba(214,255,56,0.35)";
      ctx.lineWidth = 1;
      const tickL = 12;
      // top-left
      ctx.beginPath(); ctx.moveTo(0, 0); ctx.lineTo(tickL, 0); ctx.moveTo(0, 0); ctx.lineTo(0, tickL); ctx.stroke();
      // top-right
      ctx.beginPath(); ctx.moveTo(w, 0); ctx.lineTo(w - tickL, 0); ctx.moveTo(w, 0); ctx.lineTo(w, tickL); ctx.stroke();
      // bottom corners
      ctx.beginPath(); ctx.moveTo(0, h); ctx.lineTo(tickL, h); ctx.moveTo(0, h); ctx.lineTo(0, h - tickL); ctx.stroke();
      ctx.beginPath(); ctx.moveTo(w, h); ctx.lineTo(w - tickL, h); ctx.moveTo(w, h); ctx.lineTo(w, h - tickL); ctx.stroke();

      // ---- Zone dividers + labels
      const graveEnd = w * 0.32;
      const battleEnd = w * 0.78;
      const podiumStart = battleEnd;

      // graveyard fog
      const grave = ctx.createLinearGradient(0, 0, graveEnd, 0);
      grave.addColorStop(0, "rgba(148,163,184,0.08)");
      grave.addColorStop(1, "rgba(148,163,184,0)");
      ctx.fillStyle = grave;
      ctx.fillRect(0, 0, graveEnd, h);

      // podium glow
      const podGlow = ctx.createLinearGradient(podiumStart, 0, w, 0);
      podGlow.addColorStop(0, "rgba(214,255,56,0)");
      podGlow.addColorStop(1, "rgba(214,255,56,0.12)");
      ctx.fillStyle = podGlow;
      ctx.fillRect(podiumStart, 0, w - podiumStart, h);

      // zone labels (spread horizontally to avoid overlap with HUD counters)
      ctx.font = "10px ui-monospace, monospace";
      ctx.textAlign = "left";
      ctx.fillStyle = "rgba(148,163,184,0.55)";
      ctx.fillText("GRAVEYARD", 12, 20);
      ctx.textAlign = "center";
      ctx.fillStyle = "rgba(34,211,238,0.7)";
      ctx.fillText("STRESS TEST", (graveEnd + battleEnd) / 2, 20);
      // "PODIUM" moved BELOW the top HUD row so it doesn't collide with the
      // †/◉/★ counters. Sits just above the podium blocks instead.
      ctx.textAlign = "center";
      ctx.fillStyle = "rgba(214,255,56,0.9)";
      ctx.fillText("PODIUM", (podiumStart + w) / 2, 40);

      // ---- Update + draw bots
      const alive = [];
      state.bots.forEach((b) => {
        // move
        b.x += b.vx * dt;
        b.y += b.vy * dt;
        b.vy = b.vy * 0.95 + (Math.sin(state.t * 0.05 + b.blinkPhase) * 0.15);
        if (b.y < 40) b.y = 40;
        if (b.y > h - 30) b.y = h - 30;

        // random damage — light so ~50% survive to the podium
        if (b.x > 40 && b.x < battleEnd && Math.random() < 0.018) {
          b.hp -= 4 + Math.random() * 6;
          state.sparks.push({ x: b.x, y: b.y, life: 20, color: "#F87171" });
        }
        // extra attrition inside battle zone
        if (b.x > graveEnd && b.x < battleEnd && Math.random() < 0.02) {
          b.hp -= 2;
        }

        if (b.hp <= 0 && b.deathAt < 0) {
          // die → drop into graveyard (fixed bottom strip, wraps horizontally)
          b.deathAt = state.t;
          const idx = state.graves.length;
          const cols = 5;
          const gh = 32;
          state.graves.push({
            name: b.name, sym: b.sym, fam: b.fam,
            x: 25 + (idx % cols) * 55,
            y: canvas.offsetHeight - 20 - Math.floor(idx / cols) * gh,
          });
          state.shake = 6;
        }

        if (b.deathAt < 0) {
          if (b.x >= battleEnd - 8) {
            // reached podium — champion!
            state.podium.push({
              name: b.name, sym: b.sym, fam: b.fam, color: b.color,
              tier: state.podium.length === 0 ? "LISTED"
                   : state.podium.length === 1 ? "LISTED"
                   : "LISTED",
            });
            // trim podium to 3
            if (state.podium.length > 3) state.podium.shift();
            b.deathAt = state.t; // remove from active pool
            state.shake = 4;
            for (let i = 0; i < 20; i++) {
              state.sparks.push({ x: b.x, y: b.y, life: 30 + Math.random() * 20, color: "#D6FF38" });
            }
          } else {
            alive.push(b);
            drawBot(ctx, b, state.t);
          }
        }
      });

      // Respawn quickly to keep the arena crowded
      if (alive.length < 14 && Math.random() < 0.35) alive.push(spawn());
      state.bots = alive;

      // Draw graveyard tombstones — bottom strip, most recent 15 visible
      const maxGraves = 15;
      const visibleGraves = state.graves.slice(-maxGraves);
      visibleGraves.forEach((g) => drawTombstone(ctx, g));

      // Draw podium
      drawPodium(ctx, state.podium, w, h);

      // Draw sparks
      state.sparks = state.sparks.filter((s) => {
        s.life -= dt;
        s.x += (Math.random() - 0.5) * 3;
        s.y -= 0.6 * dt;
        ctx.fillStyle = s.color + Math.floor(Math.min(1, s.life / 30) * 255).toString(16).padStart(2, "0");
        ctx.fillRect(s.x, s.y, 2, 2);
        return s.life > 0;
      });

      // screen shake decay
      if (state.shake > 0) state.shake *= 0.85;

      // stats every ~10 frames
      if (Math.floor(state.t) % 8 === 0) {
        setStats({ dead: state.graves.length, alive: state.bots.length, champs: state.podium.length });
      }

      rafRef.current = requestAnimationFrame(frame);
    }
    rafRef.current = requestAnimationFrame(frame);
    return () => { cancelAnimationFrame(rafRef.current); ro.disconnect(); };
  }, []);

  return (
    <div className="relative w-full h-[420px] rounded-xl overflow-hidden border border-[#1A1C24] bg-[#04060A]">
      <canvas ref={canvasRef} className="w-full h-full block" />
      <div className="absolute top-3 left-3 mono text-[10px] uppercase tracking-widest text-[#D6FF38] pointer-events-none">
        Illustration · most candidates are rejected
      </div>
      <div className="absolute top-3 right-3 flex gap-3 mono text-[10px] pointer-events-none">
        <span className="text-[#F87171]">✝ {stats.dead}</span>
        <span className="text-[#22D3EE]">◉ {stats.alive}</span>
        <span className="text-[#D6FF38]">★ {stats.champs}</span>
      </div>
      <div className="absolute bottom-3 right-3 mono text-[9px] text-[#64748B] pointer-events-none">
        symbolic visualisation · not live market data
      </div>
    </div>
  );
}

// ---- draw a small pixel-mech bot -------------------------------------------
function drawBot(ctx, b, t) {
  const x = b.x, y = b.y;
  const hpPct = Math.max(0, Math.min(1, b.hp / 120));
  const c = b.color;

  ctx.save();
  ctx.translate(x, y);

  // shadow
  ctx.fillStyle = "rgba(0,0,0,0.35)";
  ctx.beginPath();
  ctx.ellipse(0, 14, 10, 3, 0, 0, Math.PI * 2);
  ctx.fill();

  // tracks (bottom wheels)
  ctx.fillStyle = "#0F172A";
  ctx.fillRect(-9, 6, 18, 6);
  ctx.fillStyle = "#334155";
  const wheelOffset = (t * 2 + b.limbPhase) % 6;
  for (let i = -8; i <= 8; i += 4) {
    ctx.fillRect(i + wheelOffset - 2, 8, 2, 2);
  }

  // chassis
  ctx.fillStyle = "#0F172A";
  ctx.strokeStyle = c;
  ctx.lineWidth = 1.2;
  ctx.fillRect(-8, -4, 16, 10);
  ctx.strokeRect(-8, -4, 16, 10);

  // chest emblem (family letter)
  ctx.fillStyle = c;
  ctx.font = "bold 7px ui-monospace, monospace";
  ctx.textAlign = "center";
  ctx.fillText(b.fam, 0, 3);

  // arms (side cannons)
  ctx.fillStyle = "#1E293B";
  ctx.fillRect(-11, -1, 3, 5);
  ctx.fillRect(8, -1, 3, 5);

  // head
  ctx.fillStyle = "#0B1220";
  ctx.strokeStyle = c;
  ctx.fillRect(-5, -11, 10, 7);
  ctx.strokeRect(-5, -11, 10, 7);

  // visor (blinks)
  const blink = 0.7 + 0.3 * Math.sin(t * 0.2 + b.blinkPhase);
  ctx.fillStyle = c + Math.floor(blink * 255).toString(16).padStart(2, "0");
  ctx.fillRect(-3, -9, 6, 2);

  // antenna
  ctx.strokeStyle = c;
  ctx.beginPath();
  ctx.moveTo(0, -11);
  ctx.lineTo(0, -15);
  ctx.stroke();
  ctx.fillStyle = c;
  ctx.beginPath();
  ctx.arc(0, -16, 1.2, 0, Math.PI * 2);
  ctx.fill();

  // HP bar
  ctx.fillStyle = "rgba(15,23,42,0.9)";
  ctx.fillRect(-9, -20, 18, 2);
  ctx.fillStyle = hpPct > 0.5 ? "#22D3EE" : hpPct > 0.25 ? "#FBBF24" : "#F87171";
  ctx.fillRect(-9, -20, 18 * hpPct, 2);

  // symbol label
  ctx.fillStyle = "rgba(148,163,184,0.75)";
  ctx.font = "8px ui-monospace, monospace";
  ctx.textAlign = "center";
  ctx.fillText(b.sym, 0, -24);

  ctx.restore();
}

function drawTombstone(ctx, g) {
  ctx.save();
  ctx.translate(g.x, g.y);

  // shadow
  ctx.fillStyle = "rgba(0,0,0,0.4)";
  ctx.beginPath();
  ctx.ellipse(0, 20, 14, 3, 0, 0, Math.PI * 2);
  ctx.fill();

  // stone
  ctx.fillStyle = "#1E293B";
  ctx.strokeStyle = "#334155";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.moveTo(-11, 18);
  ctx.lineTo(-11, -4);
  ctx.arc(0, -4, 11, Math.PI, 0);
  ctx.lineTo(11, 18);
  ctx.closePath();
  ctx.fill();
  ctx.stroke();

  // cross
  ctx.strokeStyle = "#475569";
  ctx.lineWidth = 1.3;
  ctx.beginPath();
  ctx.moveTo(0, -8); ctx.lineTo(0, 2);
  ctx.moveTo(-4, -4); ctx.lineTo(4, -4);
  ctx.stroke();

  // RIP text
  ctx.fillStyle = "#64748B";
  ctx.font = "bold 6px ui-monospace, monospace";
  ctx.textAlign = "center";
  ctx.fillText("R.I.P", 0, 9);
  ctx.font = "5px ui-monospace, monospace";
  ctx.fillText(g.sym.slice(0, 6), 0, 15);
  ctx.restore();
}

function drawPodium(ctx, podium, w, h) {
  if (!podium.length) return;
  const cx = w * 0.89;
  const baseY = h - 28;

  // podium blocks (bronze/silver/gold order by index: 0=CHAMP, 1=ELITE, 2=ELITE)
  const blocks = [
    { dx:  0, height: 78, tier: "LISTED", color: "#D6FF38" },  // center tallest
    { dx: -36, height: 58, tier: "LISTED",   color: "#22D3EE" },
    { dx:  36, height: 42, tier: "LISTED",   color: "#E879F9" },
  ];

  blocks.forEach((blk, i) => {
    const bot = podium[i];
    ctx.save();
    ctx.translate(cx + blk.dx, baseY);

    // block
    ctx.fillStyle = "#0F172A";
    ctx.strokeStyle = blk.color;
    ctx.lineWidth = 1.5;
    ctx.fillRect(-16, -blk.height, 32, blk.height);
    ctx.strokeRect(-16, -blk.height, 32, blk.height);

    // rank number
    ctx.fillStyle = blk.color;
    ctx.font = "bold 14px ui-monospace, monospace";
    ctx.textAlign = "center";
    ctx.fillText(String(i + 1), 0, -blk.height / 2 + 5);

    // bot label
    if (bot) {
      // mini-bot on top
      ctx.save();
      ctx.translate(0, -blk.height - 8);
      ctx.fillStyle = bot.color;
      ctx.fillRect(-5, -4, 10, 8);
      ctx.fillStyle = "#0B1220";
      ctx.fillRect(-3, -3, 6, 2);
      ctx.fillStyle = bot.color + "ee";
      ctx.fillRect(-2, -2, 4, 1);
      ctx.restore();

      // sym label
      ctx.fillStyle = "#E5E7EB";
      ctx.font = "8px ui-monospace, monospace";
      ctx.fillText(bot.sym, 0, -blk.height - 20);
      ctx.fillStyle = blk.color;
      ctx.font = "7px ui-monospace, monospace";
      ctx.fillText(blk.tier, 0, 12);
    } else {
      ctx.fillStyle = "rgba(148,163,184,0.4)";
      ctx.font = "8px ui-monospace, monospace";
      ctx.fillText("empty", 0, -blk.height - 6);
    }
    ctx.restore();
  });
}
